import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import "./NotificationsBell.css";

import EcoIcon from "../eco-icon/EcoIcon";
import {
  listNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
  NOTIFICATIONS_CHANGED_EVENT,
  notifyNotificationsChanged,
  StaffNotification,
  unreadNotificationsCountApi,
} from "../../api/notificationsApi";

/** Contador de não lidos: a cada 2 minutos e quando a aba volta ao foco. */
const POLL_MS = 2 * 60_000;

const quando = (iso: string, agora: Date) => {
  const data = new Date(iso);
  const minutos = Math.floor((agora.getTime() - data.getTime()) / 60000);
  if (minutos < 1) return "agora";
  if (minutos < 60) return `há ${minutos} min`;
  const hora = data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (data.toDateString() === agora.toDateString()) return `hoje às ${hora}`;
  return `${data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} às ${hora}`;
};

/**
 * Sino de avisos do staff (plan-notificacoes-whatsapp, 22/10/2026): fica na
 * Topbar, então aparece em todas as telas. Os mesmos avisos vão para o WhatsApp
 * de quem tem celular cadastrado.
 */
export default function NotificationsBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<StaffNotification[] | null>(null);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await unreadNotificationsCountApi());
    } catch {
      // Sem rede ou sessão vencida: o sino só não atualiza.
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = window.setInterval(refreshCount, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
    };
  }, [refreshCount]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listNotificationsApi({ limit: 20 });
      setItems(page.items);
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível carregar os avisos.");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      loadList();
      refreshCount();
    }
  };

  // Fecha com clique fora e com Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openItem = async (item: StaffNotification) => {
    if (!item.readAt) {
      setItems((list) => list?.map((n) => (n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)) ?? null);
      setUnread((n) => Math.max(0, n - 1));
      markNotificationReadApi(item.id)
        .then(notifyNotificationsChanged)
        .catch(() => refreshCount());
    }
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  const readAll = async () => {
    try {
      await markAllNotificationsReadApi();
      const now = new Date().toISOString();
      setItems((list) => list?.map((n) => (n.readAt ? n : { ...n, readAt: now })) ?? null);
      setUnread(0);
      notifyNotificationsChanged();
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível marcar os avisos como lidos.");
    }
  };

  const agora = new Date();
  const badge = unread > 99 ? "99+" : String(unread);

  return (
    <div className="ecoBell" ref={rootRef}>
      <button
        type="button"
        className={`ecoBell__button ${open ? "is-open" : ""}`}
        onClick={toggle}
        aria-label={unread ? `Avisos: ${unread} não lido${unread === 1 ? "" : "s"}` : "Avisos"}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Avisos"
      >
        <EcoIcon name="bell" size={20} />
        {unread > 0 ? <span className="ecoBell__badge">{badge}</span> : null}
      </button>

      {open ? (
        <div className="ecoBell__panel" role="dialog" aria-label="Avisos">
          <div className="ecoBell__head">
            <strong>Avisos</strong>
            <button type="button" className="ecoBell__readAll" onClick={readAll} disabled={!unread}>
              Marcar tudo como lido
            </button>
          </div>

          <div className="ecoBell__list">
            {loading && !items ? <p className="ecoBell__empty">Carregando…</p> : null}
            {items && !items.length ? (
              <p className="ecoBell__empty">Nenhum aviso por enquanto. O resumo do dia chega aqui e no WhatsApp.</p>
            ) : null}
            {items?.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`ecoBell__item ${item.readAt ? "" : "is-unread"}`}
                onClick={() => openItem(item)}
              >
                <span className="ecoBell__itemTitle">
                  {!item.readAt ? <span className="ecoBell__dot" aria-label="Não lido" /> : null}
                  {item.title}
                </span>
                {item.body ? (
                  <span className="ecoBell__itemBody">
                    {item.body.split("\n").map((line, i) => (
                      <span key={i}>{line}</span>
                    ))}
                  </span>
                ) : null}
                <span className="ecoBell__itemWhen">{quando(item.createdAt, agora)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
